import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CountrySection from "@/components/CountrySection";
import ResultCard from "@/components/ResultCard";
import ResultCardSkeleton from "@/components/ResultCardSkeleton";
import SEOHead from "@/components/SEOHead";
import StructuredData from "@/components/StructuredData";
import { useParams, useNavigate } from "@/lib/router-compat";
import { useLotteryResultsByDate } from "@/hooks/useLotteryResults";
import { parse, format, addDays, subDays, isSameDay } from "date-fns";
import { es } from "date-fns/locale";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const ResultadosFecha = () => {
  const { fecha } = useParams();
  const navigate = useNavigate();
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Parse the fecha parameter (format: YYYY-MM-DD)
  const date = fecha ? parse(fecha, "yyyy-MM-dd", new Date()) : new Date();
  const today = new Date();
  const isToday = isSameDay(date, today);
  const minDate = new Date("2024-01-01");
  const canGoPrev = date > minDate;
  const canGoNext = !isToday && date < today;

  const goToDate = (d: Date) => navigate(`/resultados/${format(d, "yyyy-MM-dd")}`);

  const { data: results, isLoading } = useLotteryResultsByDate(date);

  const nicaraguaResults = results?.nicaraguaResults || [];
  const costaRicaResults = results?.costaRicaResults || [];

  const fechaFormateada = format(date, "d 'de' MMMM 'de' yyyy", { locale: es });

  return (
    <>
      <SEOHead
        title={`Nica y Tica ${fechaFormateada}: resultados Loto Diaria y Nuevos Tiempos | TiemposHoy`}
        description={`Resultados del ${fechaFormateada} de la Diaria Loto Nicaragua y Tiempos JPS Costa Rica. Números ganadores de ambos países.`}
        keywords={`resultados ${fechaFormateada}, resultados nicaragua ${fecha}, resultados costa rica ${fecha}`}
        canonicalUrl={`https://tiemposhoy.com/resultados/${fecha}`}
      />
      <StructuredData
        title={`Resultados del ${fechaFormateada}`}
        description={`Resultados de lotería de Nicaragua y Costa Rica del ${fechaFormateada}`}
        date={fecha}
      />
      
      <div className="min-h-screen flex flex-col">
        <Navigation />
        
        <main className="flex-1 container mx-auto px-4 py-8">
          <header className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Resultados de la Loto Diaria de Nicaragua y Nuevos Tiempos de Costa Rica del {fechaFormateada}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-6">
              🇳🇮 Diaria Loto Nicaragua · 🇨🇷 Tiempos JPS Costa Rica: números ganadores de cada sorteo
            </p>

            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToDate(subDays(date, 1))}
                disabled={!canGoPrev}
                aria-label="Día anterior"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Día anterior</span>
              </Button>

              <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2">
                    <CalendarIcon className="h-4 w-4" />
                    {format(date, "d MMM yyyy", { locale: es })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      if (d) {
                        setPopoverOpen(false);
                        goToDate(d);
                      }
                    }}
                    disabled={(d) => d > today || d < minDate}
                    locale={es}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>

              <Button
                variant="outline"
                size="sm"
                onClick={() => goToDate(addDays(date, 1))}
                disabled={!canGoNext}
                aria-label="Día siguiente"
              >
                <span className="hidden sm:inline mr-1">Día siguiente</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </header>


          {isLoading ? (
            <div className="space-y-16">
              <CountrySection
                country="nicaragua"
                title="Nicaragua"
                subtitle="Diaria Loto"
                linkHref="/tiempos-nica-hoy"
                linkText="Ver resultados actuales de Nicaragua"
              >
                <ResultCardSkeleton country="nicaragua" />
                <ResultCardSkeleton country="nicaragua" />
                <ResultCardSkeleton country="nicaragua" />
                <ResultCardSkeleton country="nicaragua" />
              </CountrySection>

              <CountrySection
                country="costa-rica"
                title="Costa Rica"
                subtitle="Tiempos JPS"
                linkHref="/tiempos-tica-hoy"
                linkText="Ver resultados actuales de Costa Rica"
              >
                <ResultCardSkeleton country="costa-rica" />
                <ResultCardSkeleton country="costa-rica" />
                <ResultCardSkeleton country="costa-rica" />
              </CountrySection>
            </div>
          ) : (!nicaraguaResults.length && !costaRicaResults.length) ? (
            <p className="text-center text-muted-foreground">No hay resultados disponibles para esta fecha.</p>
          ) : (
            <div className="space-y-16">
              <CountrySection
                country="nicaragua"
                title="Nicaragua"
                subtitle="Diaria Loto"
                linkHref="/tiempos-nica-hoy"
                linkText="Ver resultados actuales de Nicaragua"
              >
                {nicaraguaResults.map((result, index) => (
                  <ResultCard
                    key={index}
                    country="nicaragua"
                    {...result}
                  />
                ))}
              </CountrySection>

              <CountrySection
                country="costa-rica"
                title="Costa Rica"
                subtitle="Tiempos JPS"
                linkHref="/tiempos-tica-hoy"
                linkText="Ver resultados actuales de Costa Rica"
              >
                {costaRicaResults.map((result, index) => (
                  <ResultCard
                    key={index}
                    country="costa-rica"
                    {...result}
                  />
                ))}
              </CountrySection>
            </div>
          )}

          <article className="mt-16 max-w-3xl mx-auto prose prose-sm">
            <h2>¿Qué salió en la Nica y la Tica el {fechaFormateada}?</h2>
            <p>
              Consulta todos los resultados del {fechaFormateada} de la Diaria Loto de Nicaragua 
              y los Tiempos JPS de Costa Rica. Esta página histórica te permite revisar 
              los números ganadores de ambos países en una fecha específica.
            </p>
            <p>
              Para ver los resultados más recientes, visita nuestras páginas de 
              <a href="/tiempos-nica-hoy"> Nicaragua Hoy</a> o <a href="/tiempos-tica-hoy"> Costa Rica Hoy</a>.
            </p>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ResultadosFecha;
